import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../Store/Store'

export interface UserExam{
  create_date: string,
  score: number,
  status: string,
  User: {
      name: string,
      family: string
  },
  Exam: {
      name: string
  }
}

export interface Answer{
  id: number,
  answer: string,
  user_exam_id: number,
  question_id: number
}

export interface AnswerRequest{
  answer: string,
  user_exam_id: number,
  question_id: number
}

export interface PinRequest{
  status: string,
  user_exam_id: number,
  question_id: number
}

export interface PinResponse{
  message: string,
}

export interface Review{
  question: string,
  order: number,
  UserAnswers: [
    {
      answer: string
    }
  ]
  Pins : [
    {
      id: string
    }
  ]
}

export interface Answersheet{
  answers:[{
    question: string,
    order: number,
    answersheet: string,
    UserAnswers: [
      {
        answer: string
      }
    ]
  }],
  stat: {
    Total: { all: number, correct: number },
    Verbal: { all: number, correct: number, pers: number },
    Quant: { all: number, correct: number, pers: number },
    Writing: { all: number, correct: number },
  }
  exam: number
}

export type ExamType = 'GENERAL' | 'GENERAL1' | 'GENERAL2' | 'QUANT' | 'VERBAL' | 'WRITING'

export interface PerformanceScore {
  all: number
  correct: number
  pers?: number
}

export interface PerformanceSection {
  section_number: number
  section_type: 'VERBAL' | 'QUANT' | 'WRITING'
  section_name: string
  total_questions: number
  answered: number
  correct: number
  incorrect: number
  not_answered: number
}

export interface PerformanceSummary {
  exam_type: ExamType
  scores: {
    Total: PerformanceScore
    Verbal: PerformanceScore
    Quant: PerformanceScore
    Writing: PerformanceScore
  }
  sections: PerformanceSection[]
}

export interface ExamHistoryChartItem {
  user_exam_id: number
  exam_id: number | null
  exam_name: string
  date: string
  value: number
}

export interface ExamHistoryChartsResponse {
  quant: ExamHistoryChartItem[]
  verbal: ExamHistoryChartItem[]
  general_new: ExamHistoryChartItem[]
  general_old: ExamHistoryChartItem[]
}

export const userExamApi = createApi({
  reducerPath: 'userExamApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers, { getState }) => {
      // By default, if we have a token in the store, let's use that for authenticated requests
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['user-exam','exam','answer','question'],

  endpoints: (builder) => ({
    //userExams
    getAllUserExams: builder.query<UserExam[],void>({
      query: () => 'user-exam/userAll',
      providesTags: ['exam']
    }),
    startExam: builder.query<any, number>({
      query: (id) => `user-exam/start/${id}`,
      providesTags: ['user-exam']
    }),
    checkExam: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `user-exam/check/${id}`,
        method: 'GET',
      }),
    }),
    resetExam: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `user-exam/reset/${id}`,
        method: 'GET',
      }),
      invalidatesTags: ['user-exam']
    }),
    finishExam: builder.mutation<any, number>({
      query: (id) => ({
        url: `user-exam/finish/${id}`,
        method: 'GET',
      }),
      invalidatesTags: ['exam','question']
    }),
    createAnswer: builder.mutation<Answer, AnswerRequest>({
      query: (data) => ({
        url: '/user-exam/answer',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['answer','question']
    }),
    reviewAnswers: builder.query<Review[], { id: number; lastOrder?: number }>({
      query: ({ id, lastOrder }) =>
        typeof lastOrder === 'number'
          ? `user-exam/answer/review/${id}?order=${lastOrder}`
          : `user-exam/answer/review/${id}`,
      providesTags: ['answer']
    }),
    answersheet: builder.query<Answersheet, number>({
      query: (id) => `user-exam/answer/answersheet/${id}`,
    }),
    performanceSummary: builder.query<PerformanceSummary, number>({
      query: (id) => `user-exam/answer/performance-summary/${id}`,
    }),
    reviewExam: builder.query<any, number>({
      query: (id) => `user-exam/review/${id}`,
    }),
    examHistoryCharts: builder.query<ExamHistoryChartsResponse, void>({
      query: () => `user-exam/history/charts`,
    }),
    togglePin: builder.mutation<PinResponse, PinRequest>({
      query: (data) => ({
        url: '/user-exam/pin/toggle',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['question','answer']
    }),
  }),
})

export const {useGetAllUserExamsQuery ,useStartExamQuery ,useResetExamMutation ,
  useCheckExamMutation ,useCreateAnswerMutation,
   useFinishExamMutation , useReviewAnswersQuery, useAnswersheetQuery, 
   usePerformanceSummaryQuery, useReviewExamQuery, useExamHistoryChartsQuery, useTogglePinMutation } = userExamApi
